import type { ClientDocument } from './api/documents.ts';
import type { ClientFolder, ClientUpload } from './api/uploads.ts';

/**
 * The merged view of a client's documents (Arthur, 21 October, 13:45-14:00): the client's own Procura folders, with
 * the Clio copies of five Clio sections shown inside them, and every other Clio folder left where it was. A pure
 * function over the two lists the API already answers for ONE client (the Clio copies by the client's own matters;
 * the uploads by client_id): nothing here can bring another client's file in, because nothing here is given one.
 *
 * THE MAPPING IS A FIXED LIST (Arthur, 14:00), never a name comparison. A Clio section (the first folder below the
 * client's own matter folder) maps only when its name, ignoring capitals and spacing and nothing else, is one of:
 *   "Social Services"              -> Social Work
 *   "Finance"                      -> Finance
 *   "Legal"                        -> Legal
 *   "Vital Docs", "Vital Documents" -> Vital Documents
 *   "Real Estate"                  -> Real Estate (shown only where the client has it in Clio)
 * Anything else ("Legal Correspondence", the other ~50) stays separate, in the Clio explorer, even where a staff-made
 * Procura folder has the same name. Same-name merging happens only BELOW these five, at every level, at the same path:
 * a Clio subfolder and a Procura subfolder whose names match ignoring capitals and spacing are one (Arthur, 14:05).
 */
export const CLIO_SECTIONS: ReadonlyArray<readonly [string, Target]> = [
  ['Social Services', 'social_work'],
  ['Finance', 'finance'],
  ['Legal', 'legal'],
  ['Vital Docs', 'vital_documents'],
  ['Vital Documents', 'vital_documents'],
  ['Real Estate', 'real_estate'],
];
type Target = 'social_work' | 'finance' | 'legal' | 'vital_documents' | 'real_estate';

/**
 * THE SUBFOLDER MAPPING, a fixed list like the sections' (Arthur, 14:33): a Clio folder directly inside a section,
 * matched ignoring capitals and spacing only, shown under another name, combined with any Procura folder of that name
 * there. Clio's files keep From Clio; an upload lands on the Procura side, made on the first upload.
 *   Legal > "Guardianship Docs"      -> Legal > "Court Documents"
 *   Legal > "Guardianship Documents" -> Legal > "Court Documents"   (14:35: both, combined into one)
 */
export const CLIO_SUBFOLDERS: ReadonlyArray<readonly [Target, string, string]> = [
  ['legal', 'Guardianship Docs', 'Court Documents'],
  ['legal', 'Guardianship Documents', 'Court Documents'],
];

/** The read-only top-level group holding every other Clio folder, as Clio has them (Arthur, 14:31). */
export const CLIO_GROUP = 'Folders taken directly from Clio';

/** A section name as the list compares it: capitals and spacing ignored, nothing else. */
const sectionKey = (name: string) => name.toLowerCase().replace(/\s+/g, '');
const SECTION_BY_KEY = new Map(CLIO_SECTIONS.map(([n, t]) => [sectionKey(n), t]));
/** Which of the five a Clio section is, or null: it stays separate. */
export const clioSection = (name: string): Target | null => SECTION_BY_KEY.get(sectionKey(name)) ?? null;

/** A folder name below the five, compared as the five are (Arthur, 14:05): capitals and spacing ignored, nothing
 *  else ("2026" is not "FY2026"). */
const nameKey = (name: string) => name.toLowerCase().replace(/\s+/g, '');

export interface MergedFolder {
  /** Stable for React and tests: the names from the top, keyed. */
  key: string;
  name: string;
  /** Names from the top, the top folder's own name first. */
  path: string[];
  depth: number;
  /** The Procura folder, when one exists here; null where only Clio has this folder (made on the first upload). */
  procuraId: string | null;
  /** The four every client has; null for others. */
  standard: string | null;
  /** True when Clio has files at or below here. */
  fromClio: boolean;
  /** Inside the Clio group: shown as Clio has it, never uploaded to, no folder made in it. */
  readOnly: boolean;
  children: MergedFolder[];
  uploads: ClientUpload[];
  clio: ClientDocument[];
}

export interface MergedDocuments {
  /**
   * The top level: the four, Real Estate where the client has it, then "Folders taken directly from Clio" when the
   * client has any other Clio folder (Arthur, 14:31), read-only, holding them as Clio has them. (Dev may also show one
   * synthetic v1 top folder made before the top level was fixed.)
   */
  folders: MergedFolder[];
  /** Every Clio copy not in the five sections (what the group holds). */
  clioOther: ClientDocument[];
}

const ORDER: Record<string, number> = { social_work: 1, finance: 2, legal: 3, vital_documents: 4 };

export function mergeDocuments(folders: ClientFolder[], uploads: ClientUpload[], clio: ClientDocument[]): MergedDocuments {
  const node = (name: string, path: string[], procura: ClientFolder | null): MergedFolder => ({
    key: JSON.stringify(path.map(nameKey)), name, path, depth: path.length, procuraId: procura?.id ?? null,
    standard: procura?.standard ?? null, fromClio: false, readOnly: false, children: [], uploads: [], clio: [],
  });

  // The Procura tree, as it is.
  const byParent = new Map<string | null, ClientFolder[]>();
  for (const f of folders) byParent.set(f.parent_id, [...(byParent.get(f.parent_id) ?? []), f]);
  const uploadsIn = new Map<string, ClientUpload[]>();
  for (const u of uploads) uploadsIn.set(u.folder_id, [...(uploadsIn.get(u.folder_id) ?? []), u]);
  const build = (f: ClientFolder, path: string[]): MergedFolder => {
    const n = node(f.name, [...path, f.name], f);
    n.uploads = uploadsIn.get(f.id) ?? [];
    n.children = (byParent.get(f.id) ?? []).map((c) => build(c, n.path));
    return n;
  };
  const tops = (byParent.get(null) ?? []).map((f) => build(f, []));
  tops.sort((a, b) => (ORDER[a.standard ?? ''] ?? 9) - (ORDER[b.standard ?? ''] ?? 9) || a.name.localeCompare(b.name));

  // Where each of the five lands: the standard folder, or for Real Estate a Procura top folder of that exact name
  // (made by the first upload), else a Real Estate folder shown for Clio's files alone.
  const topFor = (t: Target): MergedFolder => {
    if (t !== 'real_estate') return tops.find((n) => n.standard === t)!;
    let re = tops.find((n) => n.standard === null && sectionKey(n.name) === sectionKey('Real Estate'));
    if (!re) { re = node('Real Estate', ['Real Estate'], null); tops.push(re); }
    return re;
  };

  const clioOther: ClientDocument[] = [];
  for (const d of clio) {
    // Only a copy cut at the client's OWN matter folder has a section (the explorer's rule, clientFolders(), and the
    // database's client_folder_real_estate()): one that fell back to the shared prefix (under another client's
    // number, or none) stays with the other Clio folders, whatever its first folder is called.
    const section = d.anchored && d.folder.length > 0 ? clioSection(d.folder[0]!) : null;
    const top = section ? topFor(section) : undefined;
    if (!section || !top) { clioOther.push(d); continue; }
    let at = top;
    at.fromClio = true;
    // The subfolder mapping: the first folder inside the section, if it is on the list, takes its mapped name.
    const below = d.folder.slice(1);
    const mapped = below.length > 0
      ? CLIO_SUBFOLDERS.find(([t, from]) => t === section && sectionKey(from) === sectionKey(below[0]!)) : undefined;
    if (mapped) below[0] = mapped[2];
    for (const seg of below) {
      let next = at.children.find((c) => nameKey(c.name) === nameKey(seg));
      if (!next) { next = node(seg.trim(), [...at.path, seg.trim()], null); at.children.push(next); }
      next.fromClio = true;
      at = next;
    }
    at.clio.push(d);
  }

  // Every other Clio copy, inside one read-only group, folders as Clio has them.
  if (clioOther.length > 0) {
    const group = node(CLIO_GROUP, [CLIO_GROUP], null);
    group.readOnly = true; group.fromClio = true;
    for (const d of clioOther) {
      let at = group;
      for (const seg of d.folder) {
        let next = at.children.find((c) => c.name === seg);
        if (!next) { next = node(seg, [...at.path, seg], null); next.readOnly = true; next.fromClio = true; at.children.push(next); }
        at = next;
      }
      at.clio.push(d);
    }
    tops.push(group);
  }
  return { folders: tops, clioOther };
}

/**
 * Where an upload into a merged folder goes: the deepest Procura folder along `path` (a Procura folder's parents are
 * always Procura folders, so they run unbroken from the top), and the names below it that exist only in Clio, which
 * ensure-path makes on the first upload. `id` is null only for a Real Estate folder Procura does not have yet: its top
 * folder is made first, by name, and every name after it is `below`.
 */
export function procuraTarget(folders: MergedFolder[], path: string[]): { id: string | null; topName: string; below: string[] } {
  let level = folders;
  let id: string | null = null;
  let at = 0;
  for (let i = 0; i < path.length; i++) {
    const n = level.find((c) => nameKey(c.name) === nameKey(path[i]!));
    if (!n || !n.procuraId) break;
    id = n.procuraId; at = i + 1; level = n.children;
  }
  return { id, topName: path[0] ?? '', below: path.slice(id ? at : 1) };
}
